import _extends from "@babel/runtime/helpers/esm/extends";
import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Dropdown from '../Dropdown/Dropdown';
import { MenuContext } from '../../js/MenuContext';
import { MenuMixin } from '../../mixins/components/MenuMixin';
import { focusNextElement, focusPreviousElement } from '../../util/focus';
import { KEY_CODES } from '../../constants/KEY_CODES';
export var StyledMenu = /*#__PURE__*/styled(Dropdown).withConfig({
  displayName: "StyledMenu",
  componentId: "c11n-8-64-0__sc-curt40-0"
})(["", ""], MenuMixin);
/**
 * `Menu` is built on top of the [`Dropdown`](#/Dialog%20components?id=dropdown) component and uses
 * the same [`DropdownPopper`](#/Dialog%20components?id=dropdownpopper) trigger. While dropdown
 * content can be anything, menu content should be limited to a list of
 * [`MenuItem`](#/Dialog%20components?id=menuitem) actions or
 * supporting [`MenuContent`](#/Dialog%20components?id=menucontent) text.
 *
 * Keyboard interaction is different from dropdowns in that you navigate the menu with the arrow
 * keys as opposed to with tab. Actions can be selected with enter or space.
 *
 * https://www.w3.org/TR/wai-aria-practices-1.1/#menu
 *
 * @since 7.28.0
 */

var Menu = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var defaultInnerRef = useRef();
  var innerRef = ref || defaultInnerRef;
  var activeElement = useRef();
  var onBeforeTakeFocus = props.onBeforeTakeFocus,
      onClose = props.onClose; // Capture the original focus element so we can handle tab navigation after taking focus

  var onBeforeTakeFocusCallback = useCallback(function () {
    if (typeof document !== 'undefined') {
      activeElement.current = document.activeElement;
    }

    if (onBeforeTakeFocus) {
      onBeforeTakeFocus();
    }
  }, [onBeforeTakeFocus]);
  var onKeyDownCallback = useCallback(function (e) {
    if (e.keyCode === KEY_CODES.ARROW_DOWN) {
      focusNextElement(innerRef.current);
      e.preventDefault();
    } else if (e.keyCode === KEY_CODES.ARROW_UP) {
      focusPreviousElement(innerRef.current);
      e.preventDefault();
    } else if (e.keyCode === KEY_CODES.TAB) {
      // Restore focus to the trigger and let default behavior take over
      if (activeElement.current) {
        activeElement.current.focus();
      }

      if (onClose) {
        onClose();
      }
    }
  }, [innerRef, onClose]);
  return /*#__PURE__*/React.createElement(MenuContext.Provider, {
    value: {
      onActivate: onClose
    }
  }, /*#__PURE__*/React.createElement(StyledMenu, _extends({
    innerRef: innerRef
  }, props, {
    onKeyDown: onKeyDownCallback,
    onBeforeTakeFocus: onBeforeTakeFocusCallback
  })));
});
Menu.displayName = 'Menu';
Menu.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Inherited from [`Dialog`](#/Dialog%20components?id=dialog).
   */
  onBeforeTakeFocus: PropTypes.func,

  /**
   * Function called when the menu should closed, e.g. when a
   * [`MenuItem`](#/Dialog%20components?id=menuitem) is clicked.
   *
   * Inherited from [`Dialog`](#/Dialog%20components?id=dialog).
   */
  onClose: PropTypes.func,

  /**
   * Menus have the role of `"menu"` by default per the
   * [accessibility guidelines](https://www.w3.org/TR/wai-aria-practices-1.1/#wai-aria-roles-states-and-properties-13).
   */
  role: PropTypes.string
} : {};
Menu.defaultProps = {
  onBeforeTakeFocus: undefined,
  onClose: undefined,
  role: 'menu'
};
export default Menu;